import { WebglHelper } from './webglHelper';
import { Webgl2Pipeline } from './webgl2Pipeline';
export class JointBilateralFilter {

    private vbWebgl2: Webgl2Pipeline;
    private vbHelper: WebglHelper;
    public frameBuffer: WebGLFramebuffer;
    private fragmentShader: WebGLShader;
    private stepLocation: WebGLUniformLocation;
    private radiusLocation: WebGLUniformLocation;
    private offsetLocation: WebGLUniformLocation;
    private sigmaTexelLocation: WebGLUniformLocation;
    private sigmaColorLocation: WebGLUniformLocation;
    private texelWidth: number;
    private texelHeight: number;
    private program: WebGLProgram;
    private outputCanvasWidth: number;
    private outputCanvasHeight: number;
    private segmentationMaskWidth: number;
    private segmentationMaskHeight: number;
    private gl: WebGL2RenderingContext;

    public texture: WebGLTexture;

    /**
     * @function create
     * @static
     * @public
     * @param {Webgl2Pipeline} vbWebgl2 The instance of the webgl2 class
     * @returns {JointBilateralFilter} Initialization of the class
     */
    public static create(vbWebgl2: Webgl2Pipeline) { return new JointBilateralFilter(vbWebgl2); }


    private constructor(vbWebgl2: Webgl2Pipeline) {
        this.vbWebgl2 = vbWebgl2;
        this.vbHelper = WebglHelper.create();
        this.outputCanvasWidth = this.vbWebgl2.outputCanvasElement.width;
        this.outputCanvasHeight = this.vbWebgl2.outputCanvasElement.height;
        this.segmentationMaskWidth = this.vbWebgl2.segmentationMaskCanvas.width;
        this.segmentationMaskHeight = this.vbWebgl2.segmentationMaskCanvas.height;
        this.texelWidth = 1 / this.outputCanvasWidth;
        this.texelHeight = 1 / this.outputCanvasHeight;
        this.gl = this.vbWebgl2.gl;


        const fragmentShaderSourceJointBilateralFilter = String.raw`#version 300 es
        precision highp float;
    
        uniform sampler2D u_inputFrame;
        uniform sampler2D u_segmentationMask;
        uniform vec2 u_texelSize;
        uniform float u_step;
        uniform float u_radius;
        uniform float u_offset;
        uniform float u_sigmaTexel;
        uniform float u_sigmaColor;
    
        in vec2 v_texCoord;
    
        out vec4 outColor;
    
        float gaussian(float x, float sigma) {
            float coeff = -0.5 / (sigma * sigma * 4.0 + 1.0e-6);
            return exp((x * x) * coeff);
        }
        
        void main() {
            vec2 centerCoord = v_texCoord;
            vec3 centerColor = texture(u_inputFrame, centerCoord).rgb;
            float newVal = 0.0;
    
            float spaceWeight = 0.0;
            float colorWeight = 0.0;
            float totalWeight = 0.0;
    
            // Subsample kernel space.
            for (float i = -u_radius + u_offset; i <= u_radius; i += u_step) {
                for (float j = -u_radius + u_offset; j <= u_radius; j += u_step) {
                    vec2 shift = vec2(j, i) * u_texelSize;
                    vec2 coord = vec2(centerCoord + shift);
                    vec3 frameColor = texture(u_inputFrame, coord).rgb;
                    float outVal = texture(u_segmentationMask, coord).a;
    
                    spaceWeight = gaussian(distance(centerCoord, coord), u_sigmaTexel);
                    colorWeight = gaussian(distance(centerColor, frameColor), u_sigmaColor);
                    totalWeight += spaceWeight * colorWeight;
    
                    newVal += spaceWeight * colorWeight * outVal;
                }
            }
            newVal /= totalWeight;
    
            outColor = vec4(vec3(0.0), newVal);
        }`;


        const fragmentShader = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSourceJointBilateralFilter);
        this.program = this.vbHelper.initProgram(this.gl, this.vbWebgl2.vertexShader, fragmentShader, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);

        const inputFrameLocation = this.gl.getUniformLocation(this.program, 'u_inputFrame');
        const segmentationMaskLocation = this.gl.getUniformLocation(this.program, 'u_segmentationMask');

        const texelSizeLocation = this.gl.getUniformLocation(this.program, 'u_texelSize');
        this.stepLocation = this.gl.getUniformLocation(this.program, 'u_step');
        this.radiusLocation = this.gl.getUniformLocation(this.program, 'u_radius');
        this.offsetLocation = this.gl.getUniformLocation(this.program, 'u_offset');
        this.sigmaTexelLocation = this.gl.getUniformLocation(this.program, 'u_sigmaTexel');
        this.sigmaColorLocation = this.gl.getUniformLocation(this.program, 'u_sigmaColor');

        this.frameBuffer = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer);
        this.gl.framebufferTexture2D(this.gl.DRAW_FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.vbWebgl2.personMaskTexture, 0);

        this.gl.useProgram(this.program);
        this.gl.uniform1i(inputFrameLocation, 0);
        this.gl.uniform1i(segmentationMaskLocation, 1);
        this.gl.uniform2f(texelSizeLocation, this.texelWidth, this.texelHeight);

        // Ensures default values are configured to prevent infinite
        // loop in fragment shader
        this.updateSigmaSpace(0);
        this.updateSigmaColor(0);
    }

    /**
     * @function render
     * @public
     * @description Applies the bilateral filter to all frames of the video stream
     */
    public render() {
        this.gl.viewport(0, 0, this.outputCanvasWidth, this.outputCanvasHeight);
        this.gl.useProgram(this.program);
        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.vbWebgl2.segmentationTexture);
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer);
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    }

    /**
     * @function updateSigmaSpace
     * @public
     * @param {number} sigmaSpace 
     */
    public updateSigmaSpace(sigmaSpace: number) {
        sigmaSpace *= Math.max(this.outputCanvasWidth / this.segmentationMaskWidth, this.outputCanvasHeight / this.segmentationMaskHeight);
        const kSparsityFactor = 0.66; // Higher is more sparse.
        const sparsity = Math.max(1, Math.sqrt(sigmaSpace) * kSparsityFactor);
        const step = sparsity;
        const radius = sigmaSpace;
        const offset = step > 1 ? step * 0.5 : 0;
        const sigmaTexel = Math.max(this.texelWidth, this.texelHeight) * sigmaSpace;

        this.gl.useProgram(this.program);
        this.gl.uniform1f(this.stepLocation, step);
        this.gl.uniform1f(this.radiusLocation, radius);
        this.gl.uniform1f(this.offsetLocation, offset);
        this.gl.uniform1f(this.sigmaTexelLocation, sigmaTexel);
    }

    /**
     * @function updateSigmaColor
     * @public
     * @param {number} sigmaColor 
     */
    public updateSigmaColor(sigmaColor: number) {
        this.gl.useProgram(this.program);
        this.gl.uniform1f(this.sigmaColorLocation, sigmaColor);
    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to realize the bilateral filter
     */
    public cleanUp() {
        if (this.frameBuffer) this.gl.deleteFramebuffer(this.frameBuffer);
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShader) this.gl.deleteShader(this.fragmentShader);
    }

}
